import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, Text } from '@frontend/design-system';

type BannerVariant = 'opted-out' | 'blocked';

type GroupThreadIssueBannerProps = {
  /** The type of issue to display */
  variant: BannerVariant;
  /** Whether the "Start New Thread" button should be disabled (e.g. no active participants) */
  disableNewThread?: boolean;
  /** Callback when "Start New Thread" is clicked */
  onStartNewThread?: () => void;
  /** Whether to use the compact (mini-chat) variant */
  compact?: boolean;
};

const bannerContent: Record<BannerVariant, { titleKey: string; descriptionKey: string }> = {
  'opted-out': {
    titleKey: 'One or more people are opted out',
    descriptionKey:
      'One or more people in this group has opted out of receiving text messages from you. They must reply START to continue receiving messages. To avoid a disjointed conversation with the rest of the group please start a new thread.',
  },
  blocked: {
    titleKey: 'One or more people are blocked',
    descriptionKey:
      'One or more people in this group has been previously blocked. To avoid a disjointed conversation with the rest of the group please start a new thread.',
  },
};

/**
 * Banner shown in group threads when one or more participants have an issue
 * (opted out or blocked). Displays an informational message with an option
 * to start a new thread with the remaining participants.
 *
 * Follows the same styling as the single-thread OptOutBanner.
 */
export const GroupThreadIssueBanner = ({
  variant,
  disableNewThread,
  onStartNewThread,
  compact,
}: GroupThreadIssueBannerProps) => {
  const { t } = useTranslation('thread-body');
  const { titleKey, descriptionKey } = bannerContent[variant];

  return (
    <section
      css={[
        {
          margin: `0 ${theme.spacing(3)} ${theme.spacing(3)}`,
          padding: theme.spacing(2),
          backgroundColor: theme.colors.primary10,
          border: `1px solid ${theme.colors.primary60}`,
          borderRadius: theme.borderRadius.small,
        },
        compact && {
          margin: `0 ${theme.spacing(0.5)} ${theme.spacing(0.5)}`,
          padding: theme.spacing(1),
          gap: theme.spacing(1),
        },
      ]}
    >
      <div
        css={[
          {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            gap: theme.spacing(2),
            flexWrap: 'wrap',
          },
          compact && {
            flexDirection: 'column',
            alignItems: 'center',
            gap: theme.spacing(1),
          },
        ]}
      >
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: theme.spacing(2),
            flex: 1,
          }}
        >
          <Icon size={compact ? 18 : 24} name='info' color='primary' css={{ flexShrink: 0 }} />
          <div css={{ flex: 1, minWidth: 0, maxHeight: 120, overflowY: 'auto' }}>
            <Text size={compact ? 'medium' : 'inherit'} weight='bold'>
              {t(titleKey)}
            </Text>
            <Text size={compact ? 'small' : 'medium'}>{t(descriptionKey)}</Text>
          </div>
        </div>
        <Button
          variant='tertiary'
          size={compact ? 'tiny' : 'large'}
          disabled={disableNewThread}
          onClick={onStartNewThread}
          trackingId={`group-${variant}-start-new-thread`}
          css={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          {t('Start New Thread')}
        </Button>
      </div>
    </section>
  );
};
